import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import './AppInstall.css';

// iPhone/iPad (incl. iPadOS reporting as MacIntel with touch).
const detectIOS = () => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return (
    /iphone|ipad|ipod/i.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

// Already launched from the home screen → no point offering install.
const detectStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true);

/* ---- Brand + iOS step glyphs (self-contained SVG; no external images) ---- */

const AndroidGlyph = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" fill="currentColor">
    <path d="M6 9v7a1 1 0 0 0 1 1h1v3a1 1 0 0 0 2 0v-3h4v3a1 1 0 0 0 2 0v-3h1a1 1 0 0 0 1-1V9H6zm-2 0a1 1 0 0 0-1 1v5a1 1 0 1 0 2 0v-5a1 1 0 0 0-1-1zm16 0a1 1 0 0 0-1 1v5a1 1 0 1 0 2 0v-5a1 1 0 0 0-1-1zM15.5 3.6l1-1.5a.3.3 0 0 0-.5-.3l-1.1 1.6A6.9 6.9 0 0 0 12 3c-1 0-2 .2-2.9.6L8 2.1a.3.3 0 0 0-.5.3l1 1.5A6 6 0 0 0 6 8h12a6 6 0 0 0-2.5-4.4zM9.5 6.2a.7.7 0 1 1 0-1.4.7.7 0 0 1 0 1.4zm5 0a.7.7 0 1 1 0-1.4.7.7 0 0 1 0 1.4z" />
  </svg>
);

const AppleGlyph = ({ size = 20 }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" fill="currentColor">
    <path d="M16.4 12.8c0-2.2 1.8-3.3 1.9-3.4-1-1.5-2.6-1.7-3.2-1.7-1.4-.1-2.7.8-3.3.8-.7 0-1.7-.8-2.8-.8-1.5 0-2.8.8-3.6 2.1-1.5 2.7-.4 6.6 1.1 8.8.7 1 1.6 2.2 2.7 2.2 1.1 0 1.5-.7 2.8-.7 1.3 0 1.6.7 2.8.7 1.1 0 1.9-1 2.6-2 .8-1.2 1.2-2.3 1.2-2.4-.1 0-2.4-.9-2.4-3.6zM14.3 6c.6-.7 1-1.7.9-2.7-.9 0-1.9.6-2.5 1.3-.5.6-1 1.6-.9 2.6 1 .1 1.9-.5 2.5-1.2z" />
  </svg>
);

// iOS Safari "Share" icon — box with an up arrow.
const ShareStep = () => (
  <svg viewBox="0 0 48 48" width="42" height="42" aria-hidden="true" fill="none">
    <rect x="7" y="20" width="34" height="23" rx="5" stroke="#007aff" strokeWidth="3" />
    <path d="M24 6v21" stroke="#007aff" strokeWidth="3" strokeLinecap="round" />
    <path d="M16.5 13 24 5.5 31.5 13" stroke="#007aff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// The iOS "Add to Home Screen" glyph — a rounded square with a plus.
const AddToHomeStep = () => (
  <svg viewBox="0 0 48 48" width="40" height="40" aria-hidden="true">
    <rect x="7" y="7" width="34" height="34" rx="9" fill="#eef2f8" stroke="#c9d3e2" strokeWidth="2" />
    <path d="M24 15v18M15 24h18" stroke="#007aff" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

// The blue "Add" confirmation button.
const ConfirmStep = () => (
  <svg viewBox="0 0 58 36" width="52" height="32" aria-hidden="true">
    <rect x="1" y="1" width="56" height="34" rx="9" fill="#007aff" />
    <text x="29" y="23" fontFamily="-apple-system, Helvetica, Arial, sans-serif" fontSize="15" fontWeight="700" fill="#ffffff" textAnchor="middle">
      Add
    </text>
  </svg>
);

// Browser "⋮" menu glyph (Android instructions step 1).
const MenuDotsStep = () => (
  <svg viewBox="0 0 48 48" width="40" height="40" aria-hidden="true">
    <rect x="7" y="7" width="34" height="34" rx="9" fill="#eef2f8" stroke="#c9d3e2" strokeWidth="2" />
    <circle cx="24" cy="15" r="2.6" fill="#0f172a" />
    <circle cx="24" cy="24" r="2.6" fill="#0f172a" />
    <circle cx="24" cy="33" r="2.6" fill="#0f172a" />
  </svg>
);

const IosInstallModal = ({ onClose }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      className="pwa-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Install on iPhone"
    >
      <div className="pwa-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="pwa-modal-close" onClick={onClose} aria-label="Close">
          &times;
        </button>

        <div className="pwa-modal-header">
          <span className="pwa-modal-badge">
            <AppleGlyph size={26} />
          </span>
          <h3>Add to your Home Screen</h3>
          <p>Install RDV Systems on your iPhone in three steps — from Safari.</p>
        </div>

        <ol className="pwa-steps">
          <li>
            <div className="pwa-step-art">
              <ShareStep />
            </div>
            <p className="pwa-step-text">
              <span className="pwa-step-num">1</span>
              Tap the <strong>Share</strong> button in Safari&apos;s toolbar.
            </p>
          </li>
          <li>
            <div className="pwa-step-art">
              <AddToHomeStep />
            </div>
            <p className="pwa-step-text">
              <span className="pwa-step-num">2</span>
              Scroll down and tap <strong>Add to Home Screen</strong>.
            </p>
          </li>
          <li>
            <div className="pwa-step-art">
              <ConfirmStep />
            </div>
            <p className="pwa-step-text">
              <span className="pwa-step-num">3</span>
              Tap <strong>Add</strong> — the icon lands on your Home Screen.
            </p>
          </li>
        </ol>

        <p className="pwa-modal-note">Then launch RDV Systems like any other app.</p>
      </div>
    </div>,
    document.body
  );
};

// Shown when the one-tap install prompt isn't available (e.g. Opera, or before
// Chrome's engagement heuristic fires) — install via the browser menu instead.
const AndroidInstallModal = ({ onClose }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      className="pwa-modal-overlay"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Install on Android"
    >
      <div className="pwa-modal" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="pwa-modal-close" onClick={onClose} aria-label="Close">
          &times;
        </button>

        <div className="pwa-modal-header">
          <span className="pwa-modal-badge pwa-modal-badge-android">
            <AndroidGlyph />
          </span>
          <h3>Add to your Home Screen</h3>
          <p>Install RDV Systems from your browser menu — works in Chrome, Opera, Edge and more.</p>
        </div>

        <ol className="pwa-steps">
          <li>
            <div className="pwa-step-art">
              <MenuDotsStep />
            </div>
            <p className="pwa-step-text">
              <span className="pwa-step-num">1</span>
              Open your browser&apos;s <strong>menu</strong> (the <strong>⋮</strong> or Opera button).
            </p>
          </li>
          <li>
            <div className="pwa-step-art">
              <AddToHomeStep />
            </div>
            <p className="pwa-step-text">
              <span className="pwa-step-num">2</span>
              Tap <strong>Add to Home screen</strong> (or <strong>Install app</strong>).
            </p>
          </li>
          <li>
            <div className="pwa-step-art">
              <ConfirmStep />
            </div>
            <p className="pwa-step-text">
              <span className="pwa-step-num">3</span>
              Tap <strong>Add</strong> — the RDV icon lands on your Home Screen.
            </p>
          </li>
        </ol>

        <p className="pwa-modal-note">Then launch RDV Systems like any other app.</p>
      </div>
    </div>,
    document.body
  );
};

const AppInstall = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIos, setShowIos] = useState(false);
  const [showAndroid, setShowAndroid] = useState(false);
  const [installed, setInstalled] = useState(detectStandalone());

  useEffect(() => {
    const onBeforeInstall = (e) => {
      // Stop Chrome's default mini-infobar; we drive install from our button.
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleAndroid = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      try {
        await deferredPrompt.userChoice;
      } catch (_e) {
        /* user dismissed */
      }
      setDeferredPrompt(null);
      return;
    }
    // Prompt not available (Opera, or Chrome before its engagement heuristic
    // fires) — show the manual browser-menu instructions instead.
    setShowAndroid(true);
  }, [deferredPrompt]);

  // Already running as an installed app — nothing to offer.
  if (installed) return null;

  return (
    <>
      <div className="pwa-install">
        <span className="pwa-install-label">Get the app</span>
        <div className="pwa-install-buttons">
          <button type="button" className="pwa-btn pwa-btn-android" onClick={handleAndroid}>
            <AndroidGlyph />
            <span>Android</span>
          </button>
          <button type="button" className="pwa-btn pwa-btn-apple" onClick={() => setShowIos(true)}>
            <AppleGlyph />
            <span>iPhone</span>
          </button>
        </div>
      </div>
      {showIos && <IosInstallModal onClose={() => setShowIos(false)} />}
      {showAndroid && <AndroidInstallModal onClose={() => setShowAndroid(false)} />}
    </>
  );
};

export default AppInstall;
